/**
 * screeningEconomics — deterministic, order-of-magnitude generation
 * economics for report projections. Born from the Redhead sponsor review
 * ("a lot of very good technical information and not enough economics"):
 * until the real financial model exists (platform LCOE basis is
 * 'unavailable'), reports carried NO cost perspective at all, which invites
 * readers to supply their own. This module gives every scenario-consuming
 * report an explicitly-labelled screening answer instead of silence.
 *
 * Everything derives from the FROZEN scenario result plus the SED fuel
 * price; installed-cost classes, O&M, capture cost and the 45Q credit are
 * module constants from public planning studies (EIA/NETL-family ranges,
 * IRA statute), named in the output note and replaced by quotes at FEED.
 * Pure and side-effect free; returns null rather than inventing a number
 * when the fuel price or capacity basis is unusable.
 */
import type {
  GenerationScreeningEconomicsProjection,
  GenerationScreeningEconomicsRow,
} from '../../types/generationImpact';

/** Annual fixed charge rate on installed capital (screening convention). */
export const SCREENING_FIXED_CHARGE_RATE = 0.10;

/** Non-fuel O&M allowance, $/MWh net (screening convention). */
export const SCREENING_OM_USD_PER_MWH = 8;

/** IRA 45Q credit for geologic storage, $/tonne (PW&A conditions apply). */
export const SCREENING_45Q_USD_PER_TONNE = 85;

/** Post-combustion capture cost archetype, $/tonne levelized. */
export const SCREENING_CAPTURE_COST_USD_PER_TONNE = { low: 60, high: 90 } as const;

/** Installed-cost classes, $/kW (public EIA/NETL-family planning ranges). */
export const SCREENING_CAPEX_CLASSES = [
  { id: 'simple-cycle-frame', label: 'Simple-cycle frame GT', lowUsdPerKw: 700, highUsdPerKw: 950,
    technologies: ['simple-cycle-gas-turbine'] },
  { id: 'combined-cycle-h', label: '1×1 H-class combined cycle', lowUsdPerKw: 1050, highUsdPerKw: 1350,
    technologies: ['combined-cycle-gas-turbine', 'simple-cycle-gas-turbine'] },
  { id: 'reciprocating-island', label: 'Reciprocating-engine island', lowUsdPerKw: 950, highUsdPerKw: 1300,
    technologies: ['reciprocating-engine'] },
] as const;

/** Capture retrofit adder, $/kW (screening class; configuration-dependent). */
export const SCREENING_CAPTURE_ADDER_USD_PER_KW = { low: 1000, high: 1800 } as const;

const HOURS_PER_YEAR = 8760;

function capitalUsdPerMwh(capexUsdPerKw: number, capacityFactor: number): number {
  return (capexUsdPerKw * SCREENING_FIXED_CHARGE_RATE * 1000) / (capacityFactor * HOURS_PER_YEAR);
}

const round1 = (value: number): number => Math.round(value * 10) / 10;

export interface ScreeningEconomicsInputs {
  /** SED-grounded delivered gas price; null disables the module. */
  fuelPriceUsdPerMmbtu: number | null;
  fuelPriceSource: 'sed-fuel-supply' | 'unavailable';
  /** Frozen result: full-load fuel burn and net capacity. */
  peakMmbtuPerHour: number;
  normalNetMW: number;
  /** Frozen input: duty hours (capability CF = hours / 8760). */
  annualOperatingHours: number;
  /** facilityLoadMW / normalNetMW (annualization share); null degrades. */
  facilityLoadShare: number | null;
  /** Frozen result: capability-basis capture, tonnes/yr (0 = no capture). */
  capturedCo2TonnesPerYear: number;
  /** Distinct unit technologies in the frozen scenario. */
  technologies: readonly string[];
}

/**
 * Build the indicative screening-economics projection, or null when the
 * fuel price or capacity basis cannot support even a screening number.
 */
export function deriveScreeningEconomics(
  inputs: ScreeningEconomicsInputs,
): GenerationScreeningEconomicsProjection | null {
  const price = inputs.fuelPriceUsdPerMmbtu;
  if (price == null || !Number.isFinite(price) || price <= 0) return null;
  if (!Number.isFinite(inputs.normalNetMW) || inputs.normalNetMW <= 0) return null;
  if (!Number.isFinite(inputs.peakMmbtuPerHour) || inputs.peakMmbtuPerHour <= 0) return null;

  const hours = Number.isFinite(inputs.annualOperatingHours) && inputs.annualOperatingHours > 0
    ? inputs.annualOperatingHours
    : null;
  const capabilityCf = hours == null ? null : Math.min(1, hours / HOURS_PER_YEAR);
  const servedCf = capabilityCf != null
    && inputs.facilityLoadShare != null && Number.isFinite(inputs.facilityLoadShare)
    && inputs.facilityLoadShare > 0
    ? Math.min(1, capabilityCf * inputs.facilityLoadShare)
    : null;

  const fuelMmbtuPerNetMwh = inputs.peakMmbtuPerHour / inputs.normalNetMW;
  const fuelUsdPerMwhNet = round1(fuelMmbtuPerNetMwh * price);

  const techSet = new Set(inputs.technologies);
  const classes = SCREENING_CAPEX_CLASSES.filter(
    (cls) => cls.technologies.some((tech) => techSet.has(tech)),
  );
  if (classes.length === 0) return null;

  const rowFor = (label: string, lowUsdPerKw: number, highUsdPerKw: number): GenerationScreeningEconomicsRow => ({
    label,
    lowUsdPerKw,
    highUsdPerKw,
    servedUsdPerMwhLow: servedCf == null ? null
      : round1(capitalUsdPerMwh(lowUsdPerKw, servedCf) + fuelUsdPerMwhNet + SCREENING_OM_USD_PER_MWH),
    servedUsdPerMwhHigh: servedCf == null ? null
      : round1(capitalUsdPerMwh(highUsdPerKw, servedCf) + fuelUsdPerMwhNet + SCREENING_OM_USD_PER_MWH),
    capabilityUsdPerMwhLow: capabilityCf == null ? null
      : round1(capitalUsdPerMwh(lowUsdPerKw, capabilityCf) + fuelUsdPerMwhNet + SCREENING_OM_USD_PER_MWH),
    capabilityUsdPerMwhHigh: capabilityCf == null ? null
      : round1(capitalUsdPerMwh(highUsdPerKw, capabilityCf) + fuelUsdPerMwhNet + SCREENING_OM_USD_PER_MWH),
  });

  const rows = classes.map((cls) => rowFor(cls.label, cls.lowUsdPerKw, cls.highUsdPerKw));

  const captureEnabled = Number.isFinite(inputs.capturedCo2TonnesPerYear)
    && inputs.capturedCo2TonnesPerYear > 0;
  const servedCaptureTonnes = captureEnabled && inputs.facilityLoadShare != null
    ? inputs.capturedCo2TonnesPerYear * inputs.facilityLoadShare
    : null;

  const note = 'Order-of-magnitude screening only, not a pro forma: fixed charge rate '
    + `${Math.round(SCREENING_FIXED_CHARGE_RATE * 100)}%/yr, O&M $${SCREENING_OM_USD_PER_MWH}/MWh, `
    + 'installed-cost classes from public EIA/NETL-family planning ranges, fuel from the SED price basis. '
    + 'Firm-transport demand charges, part-load heat rate, and grid-tariff structure are excluded. '
    + 'Replace with OEM/EPC quotes and a project financial model at FEED.';

  return {
    basis: 'indicative-screening',
    fixedChargeRate: SCREENING_FIXED_CHARGE_RATE,
    fuelPriceUsdPerMmbtu: price,
    fuelPriceSource: inputs.fuelPriceSource,
    fuelUsdPerMwhNet,
    omUsdPerMwh: SCREENING_OM_USD_PER_MWH,
    servedCapacityFactor: servedCf,
    capabilityCapacityFactor: capabilityCf,
    rows,
    ...(captureEnabled ? {
      capture: {
        creditUsdPerTonne: SCREENING_45Q_USD_PER_TONNE,
        captureCostUsdPerTonneLow: SCREENING_CAPTURE_COST_USD_PER_TONNE.low,
        captureCostUsdPerTonneHigh: SCREENING_CAPTURE_COST_USD_PER_TONNE.high,
        captureAdderUsdPerKwLow: SCREENING_CAPTURE_ADDER_USD_PER_KW.low,
        captureAdderUsdPerKwHigh: SCREENING_CAPTURE_ADDER_USD_PER_KW.high,
        servedCaptureTonnesPerYear: servedCaptureTonnes,
        servedCreditMUsdPerYear: servedCaptureTonnes == null ? null
          : round1((servedCaptureTonnes * SCREENING_45Q_USD_PER_TONNE) / 1e6),
        note: 'At screening precision the 45Q credit roughly covers the capture-cost archetype (wash to '
          + 'modestly positive), conditioned on a combined-cycle host, a CO2 offtake chain, and '
          + 'wage/apprenticeship qualification. FEED decides.',
      },
    } : {}),
    note,
  };
}
